import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import PhoneMockup from "./PhoneMockup";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
};

function AndroidGlyph({ className = "" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            className={className}
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M17.523 15.341c-.583 0-1.057-.475-1.057-1.058 0-.582.474-1.057 1.057-1.057.583 0 1.058.475 1.058 1.057 0 .583-.475 1.058-1.058 1.058m-11.046 0c-.583 0-1.057-.475-1.057-1.058 0-.582.474-1.057 1.057-1.057.583 0 1.057.475 1.057 1.057 0 .583-.474 1.058-1.057 1.058m11.426-6.026 2.114-3.661a.44.44 0 0 0-.16-.601.44.44 0 0 0-.601.161l-2.14 3.706C15.752 8.16 13.915 7.743 11.95 7.743s-3.802.417-5.438 1.177L4.374 5.214a.44.44 0 0 0-.601-.161.44.44 0 0 0-.16.601l2.114 3.661C2.1 11.184.206 14.231.205 17.812h23.59c-.001-3.581-1.895-6.628-5.542-8.497" />
        </svg>
    );
}

function AppleGlyph({ className = "" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            className={className}
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
    );
}

export default function Hero() {
    return (
        <section
            id="download"
            data-testid="hero-section"
            className="relative min-h-screen pt-28 lg:pt-36 pb-16 overflow-hidden"
        >
            {/* Background — soft mint/cream gradient + city texture */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_30%,rgba(81,199,173,0.10),transparent_70%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(40%_40%_at_85%_75%,rgba(81,199,173,0.06),transparent_60%)]" />
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-surface-soft" />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                <motion.div
                    initial="hidden"
                    animate="show"
                    transition={{ staggerChildren: 0.1 }}
                    className="lg:col-span-7 text-center lg:text-left"
                >
                    <motion.div
                        variants={fadeUp}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 mb-8 shadow-sm"
                        data-testid="hero-eyebrow"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-coway" />
                        Nouveau · Lancement en France
                    </motion.div>

                    <motion.h1
                        variants={fadeUp}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-black tracking-tighter leading-[0.95] text-slate-900"
                        data-testid="hero-title"
                    >
                        Partagez vos
                        <br />
                        trajets. <span className="text-coway">Réduisez</span>
                        <br />
                        vos frais.
                    </motion.h1>

                    <motion.p
                        variants={fadeUp}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="mt-7 text-lg lg:text-xl text-slate-600 max-w-xl leading-relaxed mx-auto lg:mx-0"
                        data-testid="hero-subtitle"
                    >
                        Coway vous permet de partager vos trajets en taxi ou
                        via les principales plateformes VTC avec des personnes
                        allant dans la même direction.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
                    >
                        <button
                            data-testid="btn-download-android"
                            className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-coway text-white font-semibold text-base hover:bg-coway-hover transition-all hover:scale-[1.02] active:scale-[0.98] volt-glow"
                        >
                            <AndroidGlyph className="w-5 h-5 shrink-0" />
                            <span className="flex flex-col items-start leading-none text-left">
                                <span className="text-[10px] uppercase tracking-widest opacity-80">
                                    Bientôt disponible
                                </span>
                                <span className="font-bold mt-1">
                                    Télécharger Android
                                </span>
                            </span>
                        </button>

                        <button
                            data-testid="btn-download-ios"
                            className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-white border border-slate-200 text-slate-900 font-semibold text-base hover:bg-slate-50 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-sm"
                        >
                            <AppleGlyph className="w-5 h-5 shrink-0" />
                            <span className="flex flex-col items-start leading-none text-left">
                                <span className="text-[10px] uppercase tracking-widest text-slate-500">
                                    Bientôt sur
                                </span>
                                <span className="font-bold mt-1">iPhone</span>
                            </span>
                        </button>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mt-12 grid grid-cols-3 gap-6 max-w-xl mx-auto lg:mx-0"
                        data-testid="hero-stats"
                    >
                        <Stat value="-40%" label="Économie moyenne" />
                        <Stat value="3 min" label="Pour matcher" />
                        <Stat value="100%" label="Profils vérifiés" />
                    </motion.div>
                </motion.div>

                <div className="lg:col-span-5 relative">
                    <PhoneMockup />
                </div>
            </div>

            {/* Trust strip */}
            <div className="relative mt-16 lg:mt-24 border-y border-slate-200 bg-white/70 backdrop-blur-md py-6 overflow-hidden">
                <div className="flex animate-marquee gap-16 whitespace-nowrap">
                    {[...Array(2)].map((_, i) => (
                        <div key={i} className="flex items-center gap-16">
                            {[
                                "Mise en relation simple",
                                "100% gratuit",
                                "Profils vérifiés",
                                "Support 7j/7",
                                "Avis transparents",
                                "Made in France",
                            ].map((t) => (
                                <span
                                    key={`${i}-${t}`}
                                    className="text-sm font-medium uppercase tracking-[0.25em] text-slate-500 flex items-center gap-3"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-coway" />
                                    {t}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <p className="font-display text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
                {value}
            </p>
            <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
                {label}
            </p>
        </div>
    );
}
